import React from 'react'
import {IRentModel} from './RentModel'
import DataTable, {TableColumn} from '../../../shared/components/DataTable'

type Props = {
  data: IRentModel[]
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (column: string, order: 'asc' | 'desc') => void
  loading?: boolean
}

const fmtDateTime = (v: unknown) => {
  if (v == null || v === '') return '-'
  const d = new Date(v as string | Date)
  return isNaN(d.getTime()) ? '-' : d.toLocaleString('tr-TR')
}

const cell = (row: Record<string, unknown>, key: string) => {
  const x = row[key]
  if (x === undefined || x === null || x === '') return '-'
  return String(x)
}

const splitAdSoyad = (full: string | undefined) => {
  if (!full?.trim()) return ['-', '-']
  const p = full.trim().split(/\s+/)
  if (p.length === 1) return [p[0], '-']
  return [p[0], p.slice(1).join(' ')]
}

export const RentalListTable: React.FC<Props> = ({
  data,
  currentPage,
  totalPages,
  onPageChange,
  sortBy,
  sortOrder,
  onSort,
  loading = false,
}) => {
  const rowData = (r: IRentModel) => r as unknown as Record<string, unknown>

  const columns: TableColumn<IRentModel>[] = [
    {key: 'invoiceNo', label: 'Fatura No', render: (_, row) => cell(rowData(row), 'invoiceNo')},
    {
      key: 'returnBranchName',
      label: 'Dönüş Ofisi',
      sortable: true,
      render: (_, row) => row.returnBranchName || '-',
    },
    {
      key: 'reservationSource',
      label: 'Rezervasyon Kaynağı',
      render: (_, row) =>
        row.reservationSource?.reservationSourceName ?? cell(rowData(row), 'reservationSourceName'),
    },
    {key: 'returnPlace', label: 'Dönüş Yeri', render: (_, row) => cell(rowData(row), 'returnPlace')},
    {key: 'startDate', label: 'Baş. Zaman', sortable: true, render: (v) => fmtDateTime(v)},
    {key: 'endDate', label: 'Bit. Zaman', sortable: true, render: (v) => fmtDateTime(v)},
    {key: 'carPlate', label: 'Plaka', sortable: true, render: (_, row) => row.carPlate || '-'},
    {key: 'vehicleGroupName', label: 'Araç Grubu', render: (_, row) => row.vehicleGroupName || '-'},
    {key: 'invoicedType', label: 'Faturalan Tipi', render: (_, row) => cell(rowData(row), 'invoicedType')},
    {
      key: 'firstName',
      label: 'Ad',
      render: (_, row) => splitAdSoyad(row.customerFullName)[0],
    },
    {
      key: 'lastName',
      label: 'Soyad',
      render: (_, row) => splitAdSoyad(row.customerFullName)[1],
    },
    {key: 'totalCollection', label: 'Toplam Tahsilat', render: (_, row) => cell(rowData(row), 'totalCollection')},
    {key: 'phone', label: 'Cep Tel', render: (_, row) => row.phone || '-'},
    {key: 'customerRemaining', label: 'Müşteri Kalan', render: (_, row) => cell(rowData(row), 'customerRemaining')},
    {key: 'price', label: 'Fiyat', sortable: true, render: (_, row) => String(row.price ?? '-')},
    {key: 'reservedPersonelId', label: 'Rez. Alan', render: (_, row) => row.reservedPersonelId || '-'},
    {key: 'rentalDay', label: 'Gün', render: (_, row) => String(row.rentalDay ?? '-')},
    {key: 'pickupBranchName', label: 'Çıkış Ofisi', render: (_, row) => row.pickupBranchName || '-'},
    {key: 'explanation', label: 'Açıklama', render: (_, row) => row.explanation || '-'},
    {key: 'dropFee', label: 'Drop Bedeli', render: (_, row) => cell(rowData(row), 'dropFee')},
    {key: 'status', label: 'Durum', render: (_, row) => row.status || '-'},
    {key: 'deliveredPersonelId', label: 'Teslim Eden', render: (_, row) => row.deliveredPersonelId || '-'},
    {key: 'receivedBy', label: 'Teslim Alan', render: (_, row) => cell(rowData(row), 'receivedBy')},
    {key: 'daily', label: 'Günlük', render: (_, row) => cell(rowData(row), 'daily')},
    {key: 'taxAmount', label: 'KDV', render: (_, row) => row.taxAmount ?? '-'},
    {key: 'grandTotal', label: 'Genel Toplam', render: (_, row) => row.grandTotal ?? '-'},
    {key: 'remainingAmount', label: 'Kalan', render: (_, row) => row.remainingAmount ?? '-'},
    {key: 'externalBalanceComputed', label: 'Dış Bakiye Hesaplanan', render: (_, row) => cell(rowData(row), 'externalBalanceComputed')},
    {key: 'remainingCurrency', label: 'Kalan Döviz', render: (_, row) => row.remainingCurrency ?? '-'},
    {key: 'riskPermissionLevel', label: 'Risk', render: (_, row) => row.riskPermissionLevel || '-'},
    {key: 'rentalAmount', label: 'Kira Bedeli', render: (_, row) => row.rentalAmount ?? '-'},
    {key: 'babySeat', label: 'Bebek Koltuğu', render: (_, row) => cell(rowData(row), 'babySeat')},
    {key: 'contractNo', label: 'Söz. No', render: (_, row) => cell(rowData(row), 'contractNo')},
    {key: 'navigation', label: 'Navigasyon', render: (_, row) => cell(rowData(row), 'navigation')},
    {key: 'extraServiceDesc', label: 'Ek Hizmet Açıklama', render: (_, row) => cell(rowData(row), 'extraServiceDesc')},
    {key: 'kmOverFee', label: 'Km Aşım Bedeli', render: (_, row) => cell(rowData(row), 'kmOverFee')},
    {key: 'childSeat', label: 'Çocuk Koltuğu', render: (_, row) => cell(rowData(row), 'childSeat')},
    {key: 'outFuelLevel', label: 'Çıkış Yakıt', render: (_, row) => row.outFuelLevel || '-'},
    {key: 'inFuelLevel', label: 'Donus Yakıt', render: (_, row) => row.inFuelLevel || '-'},
    {key: 'customerDistrict', label: 'Mst İlçe', render: (_, row) => cell(rowData(row), 'customerDistrict')},
    {key: 'packageService5', label: 'Paket Hizmet5', render: (_, row) => cell(rowData(row), 'packageService5')},
    {key: 'packageService6', label: 'Paket Hizmet6', render: (_, row) => cell(rowData(row), 'packageService6')},
    {key: 'insuranceTotalTl', label: 'Sigorta Toplam(TL)', render: (_, row) => cell(rowData(row), 'insuranceTotalTl')},
    {key: 'externalScore', label: 'Dış Puan', render: (_, row) => cell(rowData(row), 'externalScore')},
    {key: 'scoreType', label: 'Puan Türü', render: (_, row) => cell(rowData(row), 'scoreType')},
    {
      key: 'scoreDate',
      label: 'Puan Tarih',
      render: (_, row) => {
        const raw = rowData(row)['scoreDate']
        return fmtDateTime(raw)
      },
    },
    {key: 'invoiceControl', label: 'F. Kontrol', render: (_, row) => cell(rowData(row), 'invoiceControl')},
    {key: 'webCoupon', label: 'Web Kupon', render: (_, row) => cell(rowData(row), 'webCoupon')},
    {key: 'webICode', label: 'Web I.Kodu', render: (_, row) => cell(rowData(row), 'webICode')},
    {key: 'promotionCode', label: 'Promosyon', render: (_, row) => row.promotionCode || '-'},
    {key: 'listPrice', label: 'Liste Fiyat', render: (_, row) => cell(rowData(row), 'listPrice')},
    {key: 'upcellPrice', label: 'Upcell Fiyat', render: (_, row) => cell(rowData(row), 'upcellPrice')},
    {key: 'returnReason', label: 'Dönüş Sebebi', render: (_, row) => row.returnReason || '-'},
    {key: 'assistCompany', label: 'Assist Firma', render: (_, row) => cell(rowData(row), 'assistCompany')},
    {key: 'rentTracking', label: 'Kira Takip', render: (_, row) => cell(rowData(row), 'rentTracking')},
    {key: 'paymentMethod', label: 'Ödeme Hizmeti', render: (_, row) => row.paymentMethod || '-'},
  ]

  return (
    <DataTable
      data={data}
      columns={columns}
      showPagination={true}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      loading={loading}
      emptyMessage='Kira kaydı bulunamadı'
      tableClassName='table-sm fs-7 text-nowrap'
    />
  )
}
